import { useEffect, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Cradle, DistributionUserData } from '@solo/app-core';
import { toast, ToastContainer } from 'react-toastify';
import { Button, Flex, Text, InputWithLabel } from '@/app/ui';
import { translate } from '@/app/i18n';
import { useBlocState } from '@/app/hooks';
import { useCradle } from '@/app/contexts';

type DistributionUserDataForm = Omit<DistributionUserData, 'id' | 'userId'> & { id?: string };

const DistributionUserDataComponent = () => {
  const { authBloc, userBloc } = useCradle<Cradle>();
  const authState = useBlocState(authBloc);

  const [userDistributionData, setUserDistributionData] = useState<DistributionUserDataForm>({
    artistName: '',
    appleId: '',
    spotifyId: '',
  });
  const [showError, setShowError] = useState<boolean>(false);
  const [existingDistributionUserData, setExistingDistributionUserData] = useState<boolean>(false);

  const getUserDistributionData = async () => {
    if (authState.kind !== 'AuthenticatedAuthState' || !authState.user.id) return;
    const distributionUserData = await userBloc.getMeDistributionUserData();
    if (distributionUserData) {
      setUserDistributionData(distributionUserData);
      setExistingDistributionUserData(true);
    }
  };

  useEffect(() => {
    getUserDistributionData();
  }, [authState]);

  async function saveDistributionUserData() {
    if (
      !userDistributionData.artistName ||
      !userDistributionData.appleId ||
      !userDistributionData.spotifyId
    ) {
      setShowError(true);
      return;
    }
    if (authState.kind !== 'AuthenticatedAuthState' || !authState.user.id) return;
    try {
      if (existingDistributionUserData) {
        await userBloc.updateDistributionUserData({
          ...userDistributionData,
          userId: authState.user.id,
        });
      } else {
        await userBloc.createDistributionUserData({
          ...userDistributionData,
          userId: authState.user.id,
        });
      }
      toast.success(translate('TOAST.CHANGES_SAVED'));
    } catch (error) {
      toast.error(translate('TOAST.CHANGES_ERROR'));
    }
  }

  if (authState.kind !== 'AuthenticatedAuthState') return null;
  return (
    <Flex.Container flexDirection="column" style={{ width: '550px', paddingBottom: '20px' }}>
      <ToastContainer />
      <Flex.Container flexDirection="column" gap="2rem">
        <Text.SectionTitle>{translate('PROFILE_PAGES.USER_DISTRIBUTION')}</Text.SectionTitle>

        <Flex.Container gap="10px">
          <Flex.Item flex={1}>
            <InputWithLabel
              label={`${translate('FORMS.SPOTIFY_ID')} *`}
              placeholder={translate('FORMS.SPOTIFY_ID')}
              value={userDistributionData?.spotifyId}
              onChange={(spotifyId) =>
                setUserDistributionData({ ...userDistributionData, spotifyId })
              }
              mandatoryError={showError && !userDistributionData.spotifyId}
            />
          </Flex.Item>
          <Flex.Item flex={1}>
            <InputWithLabel
              label={`${translate('FORMS.APPLE_ID')} *`}
              placeholder={translate('FORMS.APPLE_ID')}
              value={userDistributionData?.appleId}
              onChange={(appleId) => setUserDistributionData({ ...userDistributionData, appleId })}
              mandatoryError={showError && !userDistributionData.appleId}
            />
          </Flex.Item>
        </Flex.Container>
        <Flex.Container gap="10px">
          <Flex.Item flex={1}>
            <InputWithLabel
              label={`${translate('FORMS.ARTIST_NAME')} *`}
              placeholder={translate('FORMS.ARTIST_NAME')}
              value={userDistributionData?.artistName}
              onChange={(artistName) =>
                setUserDistributionData({ ...userDistributionData, artistName })
              }
              mandatoryError={showError && !userDistributionData.artistName}
            />
          </Flex.Item>
        </Flex.Container>
      </Flex.Container>
      <Flex.Container justifyContent="flex-end" className="mt-3">
        <Button
          $size="md"
          $variant="primary"
          style={{ width: '50%' }}
          onClick={() => saveDistributionUserData()}
        >
          <Text.Span>{translate('PROFILE_INFO.SAVE_CHANGES')}</Text.Span>
        </Button>
      </Flex.Container>
    </Flex.Container>
  );
};

export default DistributionUserDataComponent;
